import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { UserProfile } from '@/types/contracts';
import { API_V1_BASE_URL } from '@/config/api';

const API_BASE = API_V1_BASE_URL;

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(localStorage.getItem('access_token'));
  const user = ref<UserProfile | null>(
    JSON.parse(localStorage.getItem('user_profile') ?? 'null') as UserProfile | null,
  );
  const loading = ref(false);

  const isAuthenticated = computed(() => !!accessToken.value);

  async function login(email: string, password: string) {
    loading.value = true;
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? 'Login failed');

      accessToken.value = json.data.accessToken;
      localStorage.setItem('access_token', json.data.accessToken);
      await fetchProfile();
    } finally {
      loading.value = false;
    }
  }

  async function register(email: string, username: string, password: string) {
    loading.value = true;
    try {
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, username, password }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error?.message ?? 'Registration failed');
      return json.data.userId;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: accessToken.value ? { Authorization: `Bearer ${accessToken.value}` } : {},
        credentials: 'include',
      });
    } finally {
      clearSession();
    }
  }

  async function refreshAccessToken() {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!res.ok) {
      clearSession();
      return false;
    }

    const json = await res.json();
    accessToken.value = json.data.accessToken;
    localStorage.setItem('access_token', json.data.accessToken);
    return true;
  }

  async function restoreSession() {
    if (accessToken.value && user.value) return;
    const ok = await refreshAccessToken();
    if (ok) await fetchProfile();
  }

  async function fetchProfile() {
    if (!accessToken.value) return;
    const res = await fetch(`${API_BASE}/users/me`, {
      headers: { Authorization: `Bearer ${accessToken.value}` },
    });
    if (res.ok) {
      const json = await res.json();
      user.value = json.data;
      localStorage.setItem('user_profile', JSON.stringify(json.data));
    }
  }

  function clearSession() {
    accessToken.value = null;
    user.value = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_profile');
  }

  return {
    accessToken,
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshAccessToken,
    restoreSession,
    fetchProfile,
    clearSession,
  };
});
